import React, { useState, useEffect, useMemo } from 'react';
import { 
    ShieldAlert, Search, Calendar, Loader2, X, ChevronLeft, 
    ChevronRight, Trash2, MessageSquare, Newspaper, Trophy,
    User as UserIcon, Clock, Shield, CheckCircle2
} from 'lucide-react';
import { fetchAllReports, deleteNewsPermanently } from '../../../services/adminService';
import { deleteComment } from '../../../services/commentService';
import { deleteNewsComment } from '../../../services/newsCommentService';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { at } from '../../../i18nAdmin';
import { calculateTimeAgo } from '../../../services/formatService';
import { ConfirmModal } from '../../Common/ConfirmModal';

export const ReportsTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [reports, setReports] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        item: any | null;
        isDeleting: boolean;
    }>({
        isOpen: false,
        item: null,
        isDeleting: false
    });

    useEffect(() => {
        loadReports();
    }, [startDate, endDate]);

    const loadReports = async () => {
        setIsLoading(true);
        try {
            const data = await fetchAllReports(startDate, endDate);
            setReports(data);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredReports = useMemo(() => {
        return reports.filter(r => {
            const matchesSearch = 
                (r.target_content?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (r.reason?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (r.reporter_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            return matchesSearch;
        });
    }, [reports, searchTerm]);

    const totalPages = Math.ceil(filteredReports.length / pageSize);
    const paginatedReports = filteredReports.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const getReportIcon = (type: string) => {
        switch(type) {
            case 'news': return <Newspaper size={18} className="text-indigo-500" />;
            case 'news_comment': return <MessageSquare size={18} className="text-rose-500" />;
            case 'match_comment': return <Trophy size={18} className="text-amber-500" />;
            default: return <Shield size={18} className="text-slate-400" />;
        }
    };

    const getTypeName = (type: string) => {
        switch(type) {
            case 'news': return at(lang, 'newsArticle');
            case 'news_comment': return at(lang, 'feedComment');
            case 'match_comment': return at(lang, 'matchTalk');
            default: return type;
        }
    };

    const handleOpenDelete = (report: any) => {
        setDeleteModal({
            isOpen: true,
            item: report,
            isDeleting: false
        });
    };

    const executeDelete = async () => {
        if (!deleteModal.item) return;
        
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            const { type, item_id } = deleteModal.item;
            
            if (type === 'news') {
                await deleteNewsPermanently(item_id);
            } else if (type === 'news_comment') {
                await deleteNewsComment(item_id);
            } else if (type === 'match_comment') {
                await deleteComment(item_id);
            }

            setReports(prev => prev.filter(r => r.item_id !== item_id));
            showToast('success', at(lang, 'contentDeleted'));
        } catch (e) {
            console.error(e);
            showToast('error', at(lang, 'deleteError'));
        } finally {
            setDeleteModal({ isOpen: false, item: null, isDeleting: false });
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <ConfirmModal 
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, item: null, isDeleting: false })}
                onConfirm={executeDelete}
                isLoading={deleteModal.isDeleting}
                title={at(lang, 'terminateIntelligence')}
                description={at(lang, 'reportsConfirmDesc')}
                confirmLabel={at(lang, 'approveDeletion')}
            />

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'securityAlerts')}</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">{at(lang, 'manageReportsDesc')}</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
                        <Calendar size={16} className="text-rose-500" />
                        <div className="flex items-center gap-3">
                            <input 
                                type="date" 
                                className="bg-transparent text-[11px] font-black uppercase text-slate-600 outline-none cursor-pointer"
                                value={startDate}
                                onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
                            />
                            <span className="text-slate-300 font-bold">—</span>
                            <input 
                                type="date" 
                                className="bg-transparent text-[11px] font-black uppercase text-slate-600 outline-none cursor-pointer"
                                value={endDate}
                                onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>
                    {(startDate || endDate) && (
                        <button 
                            onClick={() => { setStartDate(''); setEndDate(''); setCurrentPage(1); }}
                            className="p-3 bg-slate-100 text-slate-400 md:hover:text-slate-900 rounded-2xl transition-all active:scale-95"
                        >
                            <X size={18} strokeWidth={3} />
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-slate-100">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder={at(lang, 'searchReportsPlaceholder')} 
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10" 
                            value={searchTerm} 
                            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} 
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-32 flex flex-col items-center justify-center gap-4">
                        <Loader2 className="animate-spin text-rose-500" size={32} />
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 animate-pulse">Syncing Incident Logs...</span>
                    </div>
                ) : filteredReports.length === 0 ? (
                    <div className="py-40 text-center opacity-30 flex flex-col items-center gap-6">
                        <div className="w-20 h-20 rounded-[32px] bg-slate-100 flex items-center justify-center">
                            <CheckCircle2 size={48} className="text-emerald-500" />
                        </div>
                        <p className="text-sm font-black uppercase tracking-widest">System Clear: No Violations Recorded</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                    <th className="px-8 py-5 min-w-[140px]">{at(lang, 'source')}</th>
                                    <th className="px-6 py-5 min-w-[240px]">{at(lang, 'breachContextReason')}</th>
                                    <th className="px-6 py-5 min-w-[140px]">{at(lang, 'reporter')}</th>
                                    <th className="px-6 py-5 min-w-[140px]">{at(lang, 'timeline')}</th>
                                    <th className="px-8 py-5 text-right min-w-[100px]">{at(lang, 'ops')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedReports.map((report) => (
                                    <tr key={report.id} className="md:hover:bg-rose-50/20 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100 shadow-sm">
                                                    {getReportIcon(report.type)}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">{getTypeName(report.type)}</span>
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">ID: {report.id.split('-').pop().substring(0, 8)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex flex-col max-w-sm">
                                                <p className="text-[11px] font-black text-slate-900 truncate uppercase tracking-tight mb-2 italic">
                                                    "{report.target_content || 'Asset not found'}"
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <ShieldAlert size={12} className="text-rose-500" />
                                                    <span className="text-xs font-bold text-slate-500 leading-tight line-clamp-1">{report.reason || 'Security violation'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shadow-sm border border-white">
                                                    <UserIcon size={12} className="text-slate-400" />
                                                </div>
                                                <span className="text-xs font-black text-slate-700 uppercase tracking-tight truncate max-w-[100px]">{report.reporter_name || 'Anonymous'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex flex-col">
                                                <span className="text-[11px] font-black text-slate-500 tabular-nums">{new Date(report.created_at).toLocaleDateString()}</span>
                                                <span className="text-[9px] font-bold text-slate-300 uppercase tracking-tighter mt-0.5">{calculateTimeAgo(report.created_at)}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <button 
                                                onClick={() => handleOpenDelete(report)}
                                                className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                title={at(lang, 'delete')}
                                            >
                                                <Trash2 size={18} strokeWidth={2.5} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Incident Page {currentPage} of {totalPages || 1}</p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
